import { plansService } from "services";
import {
	extractCreatedPlanId,
	findPlanAssignmentId,
} from "utils/undo/planUndoHelpers";

const REQUEST_EXECUTORS = {
	plansService: {
		add: (...args) => plansService.add(...args),
		remove: (...args) => plansService.remove(...args),
		update: (...args) => plansService.update(...args),
		updateAssignment: (...args) => plansService.updateAssignment(...args),
		updateMultiple: (...args) => plansService.updateMultiple(...args),
		removeMultiple: (...args) => plansService.removeMultiple(...args),
	},
};

const ALREADY_ASSIGNED_RE =
	/already.?assign|ya se ha asignad|shift_already_assigned/i;

export function isAlreadyAssignedError(response) {
	if (response?.ok !== false) {
		return false;
	}

	const text = [
		response.errors?.[0]?.key,
		response.errors?.[0]?.description,
		response.key,
	]
		.filter(Boolean)
		.join(" ");

	return ALREADY_ASSIGNED_RE.test(text);
}

function restoreLookupGroupId(body) {
	if (body?.group_id) {
		return body.group_id;
	}

	if (typeof window === "undefined") {
		return undefined;
	}

	const match = window.location?.pathname?.match(/\/plans\/(\d+)/);
	return match ? Number(match[1]) : undefined;
}

function createPayloadFromRestoreBody(body) {
	if (!body || typeof body !== "object") {
		return body;
	}

	const { group_id, ...payload } = body;
	return payload;
}

function assignmentRowsFromResponse(response) {
	if (Array.isArray(response?.data)) {
		return response.data;
	}
	if (Array.isArray(response)) {
		return response;
	}
	return [];
}

export async function findExistingAssignmentId(body) {
	if (!body?.employee_id || !body?.day || body?.shift_id == null) {
		return null;
	}

	const response = await plansService.getPlanAssignments({
		group_id: restoreLookupGroupId(body),
		start: body.day,
		end: body.day,
		update_employee_ids: [body.employee_id],
	});

	if (response?.ok === false) {
		return null;
	}

	return findPlanAssignmentId(assignmentRowsFromResponse(response), {
		employeeId: body.employee_id,
		day: body.day,
		shiftId: body.shift_id,
	});
}

async function wait(ms) {
	if (!ms) {
		return;
	}
	await new Promise((resolve) => setTimeout(resolve, ms));
}

async function findExistingAssignmentIdWithRetry(body) {
	for (const delay of [0, 150, 300]) {
		await wait(delay);
		const existingId = await findExistingAssignmentId(body);
		if (existingId) {
			return existingId;
		}
	}
	return null;
}

async function applyDesiredLock(assignmentId, body) {
	if (typeof body?.locked !== "boolean") {
		return { ok: true, id: assignmentId };
	}

	return plansService.update({ id: assignmentId, locked: body.locked });
}

/**
 * After a work shift leaves a cell, the backend often recreates the default
 * rest as locked. Unlock it when present; do not create a new rest.
 */
export async function unlockDefaultRestIfPresent(body) {
	if (!body?.employee_id || !body?.day || body?.shift_id == null) {
		return { ok: true };
	}

	const existingId = await findExistingAssignmentIdWithRetry(body);
	if (!existingId) {
		return { ok: true };
	}

	return applyDesiredLock(existingId, { locked: false });
}

/**
 * Restore an assignment after undo.
 *
 * POST /plans/ requires locked:true. Deleting a replacement shift often makes
 * the backend recreate the default rest, so a second POST hits
 * "el turno ya se ha asignado". Prefer the existing row and PATCH lock state.
 */
export async function restorePlanAssignment(body) {
	const existingId = await findExistingAssignmentId(body);
	if (existingId) {
		return applyDesiredLock(existingId, body);
	}

	const wantsUnlocked = body?.locked === false;
	const createBody = createPayloadFromRestoreBody(
		wantsUnlocked ? { ...body, locked: true } : body
	);
	const created = await plansService.add(createBody);

	if (created?.ok === false && isAlreadyAssignedError(created)) {
		const recoveredId = await findExistingAssignmentIdWithRetry(body);
		if (recoveredId) {
			return applyDesiredLock(recoveredId, body);
		}
		return created;
	}

	if (created?.ok === false || !wantsUnlocked) {
		return created;
	}

	const createdId = extractCreatedPlanId(created, {
		day: body.day,
		shiftId: body.shift_id,
		employeeId: body.employee_id,
	});

	if (!createdId) {
		return created;
	}

	return plansService.update({ id: createdId, locked: false });
}

export const addPlanHonoringLock = restorePlanAssignment;

export async function executeInverseRequests(requests = []) {
	let lastResponse = { ok: true };

	for (const request of requests) {
		const { service, method, args = [] } = request;

		if (service === "plansService" && method === "add") {
			lastResponse = await restorePlanAssignment(args[0]);
			if (lastResponse?.ok === false) {
				return lastResponse;
			}
			continue;
		}

		const executor = REQUEST_EXECUTORS[service]?.[method];

		if (!executor) {
			return {
				ok: false,
				errors: [{ description: `Unknown undo request: ${service}.${method}` }],
			};
		}

		lastResponse = await executor(...args);
		if (lastResponse?.ok === false) {
			return lastResponse;
		}
	}

	return lastResponse;
}
